import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { bisaEditCaptionTiktok, EditCaptionTiktok } from '../components/TiktokCaption';
import { colors } from '../theme';

const API = 'https://api.sm-media.id';

// Status per akun IG dari status asli di Instagram (ig_state dari Postiz, 2026-10-24):
// 'done' tanpa ig_state = sudah dikirim, belum tayang.
function igPostStatus(p) {
  if (p.status === 'failed') return { label: '❌ Gagal', warna: 'failed' };
  if (p.status === 'scheduled') return { label: '🕒 Terjadwal', warna: 'pending' };
  if (p.ig_state === 'PUBLISHED' || p.permalink) return { label: '✅ Tayang', warna: 'done' };
  if (p.status === 'done' || p.status === 'uploading') return { label: '⏳ Memproses', warna: 'processing' };
  return { label: '⏳', warna: 'pending' };
}
function igRingkas(job) {
  const posts = job.ig_posts || [];
  if (!posts.length) return job.ig_status === 'success' ? 'Berhasil' : job.ig_status === 'failed' ? 'Gagal' : '-';
  const label = posts.map(p => igPostStatus(p).warna);
  if (label.includes('done')) return 'Berhasil';
  if (label.includes('processing')) return 'Proses';
  if (label.includes('pending')) return 'Terjadwal';
  return 'Gagal';
}

export default function Laporan() {
  const [reports, setReports] = useState([]);
  const [aiReport, setAiReport] = useState({ detail: [], summary: { total_credits: 0, total_requests: 0 } });
  const [tiktokAccounts, setTiktokAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [activeSection, setActiveSection] = useState('jobs');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [stats, setStats] = useState({ total: 0, done: 0, failed: 0, channels_uploaded: 0, ig_success: 0 });

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from', fromDate);
      if (toDate) params.append('to', toDate);

      const [jobRes, aiRes, ttRes] = await Promise.all([
        axios.get(`${API}/api/jobs/report?${params.toString()}`),
        axios.get(`${API}/api/jobs/report/ai?${params.toString()}`),
        axios.get(`${API}/api/tiktok/accounts`).catch(() => ({ data: { accounts: [] } })),
      ]);

      const data = jobRes.data;
      setReports(data);

      let done = 0, failed = 0, channels_uploaded = 0, ig_success = 0;
      data.forEach(job => {
        if (job.status === 'done') done++;
        if (job.status === 'failed') failed++;
        if (igRingkas(job) === 'Berhasil') ig_success++;
        if (job.channels) {
          job.channels.forEach(ch => {
            if (ch.status === 'done') channels_uploaded++;
          });
        }
      });
      setStats({ total: data.length, done, failed, channels_uploaded, ig_success });
      setAiReport(aiRes.data);
      setTiktokAccounts(ttRes.data.accounts || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [fromDate, toDate]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const downloadCSV = () => {
    const rows = [
      ['Judul', 'URL Sumber', 'Tanggal', 'Status', 'Platform', 'Channel YT Berhasil', 'Channel YT Gagal', 'Total Channel YT', 'IG Status', 'Auto Subtitle AI', 'Durasi']
    ];
    reports.forEach(job => {
      const ytSuccess = job.channels?.filter(c => c.status === 'done').length || 0;
      const ytFailed = job.channels?.filter(c => c.status === 'failed').length || 0;
      const duration = job.started_at && job.finished_at
        ? Math.round((new Date(job.finished_at) - new Date(job.started_at)) / 1000) + 's'
        : '-';
      rows.push([
        `"${(job.title || 'Tanpa Judul').replace(/"/g, '""')}"`,
        job.youtube_url,
        new Date(job.created_at).toLocaleString('id-ID'),
        job.status,
        job.platforms === 'all' ? 'YouTube + Instagram + TikTok' : job.platforms === 'both' ? 'YouTube + Instagram' : job.platforms === 'tiktok' ? 'TikTok' : job.platforms === 'youtube' ? 'YouTube' : job.platforms === 'instagram' ? 'Instagram' : 'YouTube',
        ytSuccess,
        ytFailed,
        job.total_channel || 0,
        job.ig_status || '-',
        (job.auto_subs === true || job.auto_subs === 't' || job.auto_subs === 'true') ? 'Ya' : 'Tidak',
        duration,
      ]);
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const dateRange = fromDate && toDate ? `_${fromDate}_sd_${toDate}` : '';
    a.href = url;
    a.download = `laporan_upload${dateRange}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAICSV = () => {
    const rows = [['Tanggal', 'Model', 'Total Request', 'Kredit Terpakai', 'Input Tokens', 'Output Tokens']];
    aiReport.detail.forEach(row => {
      rows.push([
        new Date(row.date).toLocaleDateString('id-ID'),
        row.model,
        row.total_requests,
        row.total_credits,
        row.total_input_tokens,
        row.total_output_tokens,
      ]);
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `laporan_ai_credits.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadTiktokCSV = () => {
    const rows = [['Display Name', 'Username', 'Open ID', 'Status', 'Needs Reconnect', 'Terhubung Sejak']];
    tiktokAccounts.forEach(acc => {
      rows.push([
        acc.display_name || '-',
        acc.username ? `@${acc.username}` : '-',
        acc.open_id,
        acc.is_active ? 'Aktif' : 'Nonaktif',
        acc.needs_reconnect ? 'Ya' : 'Tidak',
        new Date(acc.created_at).toLocaleDateString('id-ID'),
      ]);
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `laporan_tiktok_accounts.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statusColor = (s) => {
    if (s === 'done' || s === 'success') return { bg: '#064e3b', color: '#10b981' };
    if (s === 'failed') return { bg: '#450a0a', color: '#ef4444' };
    if (s === 'processing') return { bg: '#1e3a5f', color: '#3b82f6' };
    return { bg: '#451a03', color: '#f59e0b' };
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return '-';
    const diff = Math.round((new Date(end) - new Date(start)) / 1000);
    if (diff < 60) return `${diff}s`;
    return `${Math.floor(diff / 60)}m ${diff % 60}s`;
  };

  const filtered = reports.filter(j => filter === 'all' || j.status === filter);

  const getDownloadHandler = () => {
    if (activeSection === 'ai') return downloadAICSV;
    if (activeSection === 'tiktok') return downloadTiktokCSV;
    return downloadCSV;
  };

  return (
    <div style={{ paddingBottom: 40 }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#f1f5f9' }}>📊 Laporan</h1>
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Riwayat upload dan penggunaan AI</p>
      </div>

      {/* Section Toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { id: 'jobs', label: '📤 Laporan Upload' },
          { id: 'ai', label: '✨ Penggunaan AI' },
          { id: 'tiktok', label: '🎵 Akun TikTok' },
        ].map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: activeSection === s.id ? 700 : 400, background: activeSection === s.id ? 'linear-gradient(135deg,#f59e0b,#fbbf24)' : '#1a2235', color: activeSection === s.id ? '#000' : '#94a3b8' }}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Date Filter — sembunyikan di TikTok section */}
      {activeSection !== 'tiktok' && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center', background: '#111827', padding: 14, borderRadius: 10, border: '1px solid #1e2d45' }}>
          <span style={{ fontSize: 13, color: '#94a3b8' }}>Filter Tanggal:</span>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #1e2d45', background: '#1a2235', color: '#f1f5f9', fontSize: 13 }} />
          <span style={{ color: '#64748b', fontSize: 13 }}>s/d</span>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #1e2d45', background: '#1a2235', color: '#f1f5f9', fontSize: 13 }} />
          <button onClick={fetchReports}
            style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: 'linear-gradient(135deg,#f59e0b,#fbbf24)', color: '#000', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            Cari
          </button>
          {(fromDate || toDate) && (
            <button onClick={() => { setFromDate(''); setToDate(''); }}
              style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #1e2d45', background: 'transparent', color: '#94a3b8', fontSize: 13, cursor: 'pointer' }}>
              Reset
            </button>
          )}
          <button onClick={getDownloadHandler()}
            style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #10b981', background: 'transparent', color: '#10b981', fontSize: 13, cursor: 'pointer', marginLeft: 'auto' }}>
            ⬇ Download CSV
          </button>
        </div>
      )}

      {/* TikTok section — punya download button sendiri */}
      {activeSection === 'tiktok' && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <button onClick={downloadTiktokCSV}
            style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #10b981', background: 'transparent', color: '#10b981', fontSize: 13, cursor: 'pointer' }}>
            ⬇ Download CSV
          </button>
        </div>
      )}

      {/* ─── SECTION: JOBS ─────────────────────────────────────────────── */}
      {activeSection === 'jobs' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 20 }}>
            {[
              { label: 'Total Job', value: stats.total, color: '#f59e0b', icon: '📋' },
              { label: 'Berhasil', value: stats.done, color: '#10b981', icon: '✅' },
              { label: 'Gagal', value: stats.failed, color: '#ef4444', icon: '❌' },
              { label: 'Upload Channel', value: stats.channels_uploaded, color: '#3b82f6', icon: '📺' },
              { label: 'IG Berhasil', value: stats.ig_success, color: '#a855f7', icon: '📸' },
            ].map(s => (
              <div key={s.label} style={{ background: '#111827', border: '1px solid #1e2d45', borderRadius: 10, padding: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 20, marginBottom: 4 }}>{s.icon}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {[
              { id: 'all', label: 'Semua' },
              { id: 'done', label: '✅ Berhasil' },
              { id: 'failed', label: '❌ Gagal' },
              { id: 'pending', label: '⏳ Pending' },
              { id: 'processing', label: '🔄 Processing' },
            ].map(f => (
              <button key={f.id} onClick={() => setFilter(f.id)}
                style={{ padding: '5px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: filter === f.id ? 600 : 400, background: filter === f.id ? 'linear-gradient(135deg,#f59e0b,#fbbf24)' : '#1a2235', color: filter === f.id ? '#000' : '#94a3b8' }}>
                {f.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
              <div>Belum ada laporan</div>
            </div>
          ) : filtered.map(job => (
            <div key={job.id} style={{ background: '#111827', border: '1px solid #1e2d45', borderRadius: 12, padding: 16, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#f1f5f9', marginBottom: 4 }}>{job.title || 'Tanpa Judul'}</div>
                  <a href={job.youtube_url} target="_blank" rel="noreferrer" style={{ color: '#f59e0b', fontSize: 11, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.youtube_url}</a>
                </div>
                <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, flexShrink: 0, ...statusColor(job.status) }}>
                  {job.status === 'done' ? '✅ Berhasil' : job.status === 'failed' ? '❌ Gagal' : job.status === 'processing' ? '🔄 Processing' : '⏳ Pending'}
                </span>
              </div>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 11, color: '#64748b', marginBottom: 10 }}>
                <span>📅 {new Date(job.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                <span>🕐 {new Date(job.created_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}</span>
                {job.started_at && job.finished_at && <span>⏱ {formatDuration(job.started_at, job.finished_at)}</span>}
                <span>📺 {job.yt_success}/{job.total_channel} channel YT</span>
                {job.platforms?.includes('instagram') || job.platforms === 'both' || job.platforms === 'all' ? (
                  <span style={{ color: igRingkas(job) === 'Berhasil' ? '#10b981' : igRingkas(job) === 'Gagal' ? '#ef4444' : colors.textSecondary }}>
                    📸 IG: {igRingkas(job)}
                  </span>
                ) : null}
                {job.platforms === 'tiktok' || job.platforms === 'all' ? (
                  <span style={{ color: job.tiktok_status === 'success' ? '#10b981' : '#ef4444' }}>
                    🎵 TikTok: {job.tiktok_status === 'success' ? 'Berhasil' : job.tiktok_status === 'failed' ? 'Gagal' : job.tiktok_status === 'pending' ? 'Proses' : job.tiktok_status === 'scheduled' ? 'Terjadwal' : '-'}
                  </span>
                ) : null}
                {bisaEditCaptionTiktok(job) && <EditCaptionTiktok job={job} onSaved={cap => setReports(rs => rs.map(x => x.id === job.id ? { ...x, tiktok_caption: cap } : x))} />}
                <span style={{ color: (job.auto_subs === true || job.auto_subs === 't' || job.auto_subs === 'true') ? '#a855f7' : '#64748b' }}>
                  🎬 Subs: {(job.auto_subs === true || job.auto_subs === 't' || job.auto_subs === 'true') ? 'AI On' : 'Off'}
                </span>
              </div>

              {job.error_message && (
                <div style={{ background: '#450a0a', border: '1px solid #ef4444', borderRadius: 8, padding: '6px 10px', fontSize: 11, color: '#ef4444', marginBottom: 10 }}>
                  ⚠️ {job.error_message}
                </div>
              )}

              {job.channels && job.channels.length > 0 && (
                <div style={{ borderTop: '1px solid #1e2d45', paddingTop: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 8 }}>Detail per Channel YouTube:</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {job.channels.map((ch, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#1a2235', borderRadius: 8, flexWrap: 'wrap' }}>
                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: ch.status === 'done' ? 'linear-gradient(135deg,#10b981,#059669)' : ch.status === 'failed' ? 'linear-gradient(135deg,#ef4444,#dc2626)' : '#1e2d45', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                          {ch.channel_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 500, color: '#f1f5f9' }}>{ch.channel_name || 'Unknown'}</div>
                          {ch.error && <div style={{ fontSize: 10, color: '#ef4444', marginTop: 1 }}>{ch.error}</div>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                          <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 999, ...statusColor(ch.status) }}>
                            {ch.status === 'done' ? '✅ Upload' : ch.status === 'failed' ? '❌ Gagal' : '⏳'}
                          </span>
                          {ch.video_id && (
                            <a href={`https://youtu.be/${ch.video_id}`} target="_blank" rel="noreferrer" style={{ fontSize: 10, color: '#f59e0b', textDecoration: 'none', padding: '2px 6px', border: '1px solid #f59e0b', borderRadius: 4 }}>▶ Tonton</a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(job.ig_posts || []).length > 0 && (
                <div style={{ borderTop: '1px solid #1e2d45', paddingTop: 10, marginTop: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 8 }}>Detail per akun Instagram:</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {job.ig_posts.map((p, i) => {
                      const st = igPostStatus(p);
                      return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#1a2235', borderRadius: 8, flexWrap: 'wrap' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 500, color: '#f1f5f9' }}>@{p.username || 'instagram'}</div>
                            {p.status === 'failed' && p.error && <div style={{ fontSize: 10, color: '#ef4444', marginTop: 1, overflowWrap: 'anywhere' }}>{p.error}</div>}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 999, ...statusColor(st.warna) }}>{st.label}</span>
                            {p.permalink && (
                              <a href={p.permalink} target="_blank" rel="noreferrer" style={{ fontSize: 10, color: '#f59e0b', textDecoration: 'none', padding: '2px 6px', border: '1px solid #f59e0b', borderRadius: 4 }}>↗ Lihat di Instagram</a>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {!(job.ig_posts || []).length && job.ig_post_id && (
                <div style={{ borderTop: '1px solid #1e2d45', paddingTop: 10, marginTop: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 6 }}>Instagram:</div>
                  <div style={{ fontSize: 11, color: '#10b981' }}>✅ Post ID: {job.ig_post_id}</div>
                </div>
              )}
              {!(job.ig_posts || []).length && job.ig_error && (
                <div style={{ borderTop: '1px solid #1e2d45', paddingTop: 10, marginTop: 10 }}>
                  <div style={{ fontSize: 11, color: '#ef4444' }}>❌ IG Error: {job.ig_error}</div>
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {/* ─── SECTION: AI CREDITS ───────────────────────────────────────── */}
      {activeSection === 'ai' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 20 }}>
            {[
              { label: 'Total Kredit Terpakai', value: aiReport.summary?.total_credits || 0, color: '#f59e0b', icon: '✨' },
              { label: 'Total Request', value: aiReport.summary?.total_requests || 0, color: '#3b82f6', icon: '🤖' },
            ].map(s => (
              <div key={s.label} style={{ background: '#111827', border: '1px solid #1e2d45', borderRadius: 10, padding: 16, textAlign: 'center' }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>{s.icon}</div>
                <div style={{ fontSize: 26, fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading...</div>
          ) : aiReport.detail.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🤖</div>
              <div>Belum ada penggunaan AI</div>
            </div>
          ) : (
            <div style={{ background: '#111827', border: '1px solid #1e2d45', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#1a2235' }}>
                      {['Tanggal', 'Model', 'Request', 'Kredit', 'Input Token', 'Output Token'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: '#94a3b8', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {aiReport.detail.map((row, i) => (
                      <tr key={i} style={{ borderTop: '1px solid #1e2d45' }}>
                        <td style={{ padding: '10px 14px', color: '#f1f5f9' }}>{new Date(row.date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                        <td style={{ padding: '10px 14px', color: '#a855f7', fontWeight: 600 }}>{row.model}</td>
                        <td style={{ padding: '10px 14px', color: '#3b82f6' }}>{row.total_requests}x</td>
                        <td style={{ padding: '10px 14px', color: '#f59e0b', fontWeight: 700 }}>{row.total_credits}</td>
                        <td style={{ padding: '10px 14px', color: '#64748b' }}>{Number(row.total_input_tokens).toLocaleString()}</td>
                        <td style={{ padding: '10px 14px', color: '#64748b' }}>{Number(row.total_output_tokens).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ─── SECTION: TIKTOK ───────────────────────────────────────────── */}
      {activeSection === 'tiktok' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 20 }}>
            {[
              { label: 'Total Akun', value: tiktokAccounts.length, color: '#f59e0b', icon: '🎵' },
              { label: 'Aktif', value: tiktokAccounts.filter(a => !a.needs_reconnect).length, color: '#10b981', icon: '✅' },
              { label: 'Perlu Reconnect', value: tiktokAccounts.filter(a => a.needs_reconnect).length, color: '#ef4444', icon: '⚠️' },
            ].map(s => (
              <div key={s.label} style={{ background: '#111827', border: '1px solid #1e2d45', borderRadius: 10, padding: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 20, marginBottom: 4 }}>{s.icon}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading...</div>
          ) : tiktokAccounts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🎵</div>
              <div>Belum ada akun TikTok terhubung</div>
              <div style={{ fontSize: 12, marginTop: 6 }}>Hubungkan akun TikTok melalui menu Hubungkan channel</div>
            </div>
          ) : (
            <div style={{ background: '#111827', border: '1px solid #1e2d45', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#1a2235' }}>
                      {['Akun', 'Username', 'Open ID', 'Status', 'Terhubung Sejak'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: '#94a3b8', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tiktokAccounts.map((acc, i) => (
                      <tr key={i} style={{ borderTop: '1px solid #1e2d45' }}>
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 28, height: 28, borderRadius: '50%', background: acc.avatar_url ? 'transparent' : 'linear-gradient(135deg,#000,#333)', flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700 }}>
                              {acc.avatar_url ? <img src={acc.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (acc.display_name?.charAt(0) || 'T')}
                            </div>
                            <span style={{ color: '#f1f5f9', fontWeight: 500 }}>{acc.display_name || '-'}</span>
                          </div>
                        </td>
                        <td style={{ padding: '10px 14px', color: '#64748b' }}>{acc.username ? `@${acc.username}` : '-'}</td>
                        <td style={{ padding: '10px 14px', color: '#475569', fontSize: 11 }}>{acc.open_id?.substring(0, 16)}...</td>
                        <td style={{ padding: '10px 14px' }}>
                          {acc.needs_reconnect
                            ? <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, background: '#450a0a', color: '#ef4444', fontWeight: 600 }}>⚠️ Perlu Reconnect</span>
                            : <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, background: '#064e3b', color: '#10b981', fontWeight: 600 }}>✅ Aktif</span>
                          }
                        </td>
                        <td style={{ padding: '10px 14px', color: '#64748b', whiteSpace: 'nowrap' }}>{new Date(acc.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
